import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { FormalCreateNoteRequestDTO } from 'src/app/model/observation/formal/formal-create-note-request-dto.model';
import { FormalDistributeNotesDTO } from 'src/app/model/observation/formal/formal-distribute-notes-dto.model';
import { FormalNoteDTO } from 'src/app/model/observation/formal/formal-note-dto.model';
import { FormalUpdateNoteRequestDTO } from 'src/app/model/observation/formal/formal-update-note-request-dto.model';
import { FormalService } from 'src/app/services/Observation/formal/formal.service';
import { RequestQueue } from 'src/app/utilities/request-queue/request-queue.util';
import { ObservationCommentService } from '../../observation-comments/services/observation-comment/observation-comment.service';

@Injectable()
export class ScriptingToolService {
    private reloadQueue: RequestQueue<FormalNoteDTO[]>  = new RequestQueue<FormalNoteDTO[]>();
    public evidence: BehaviorSubject<FormalNoteDTO[]> = new BehaviorSubject<FormalNoteDTO[]>([]);

    constructor(
        private formalService: FormalService, 
        private observationCommentService: ObservationCommentService
    ) { }

    public get isLoading(): boolean {
        return this.reloadQueue.isLoading;
    }

    public get isLoadingObservable(): Observable<boolean> {
        return this.reloadQueue.isLoadingObservable;
    }

    initialize(evidence: FormalNoteDTO[]) {
        this.evidence.next(evidence);
    }

    saveEvidence = (note: FormalCreateNoteRequestDTO): Promise<FormalNoteDTO[]> => 
        this.reloadQueue.generateAndEnqueRequest(this.generateSaveEvidenceRequest(note));

    updateEvidence = (note: FormalUpdateNoteRequestDTO, observationId: string): Promise<FormalNoteDTO[]> => 
        this.reloadQueue.generateAndEnqueRequest(this.generatUpdateEvidenceRequest(note, observationId));

    deleteEvidence = (noteId: string, observationId: string): Promise<FormalNoteDTO[]> => 
        this.reloadQueue.generateAndEnqueRequest(this.generateDeleteEvidenceRequest(noteId, observationId));

    updateDistributeEvidenceDraft = (formalId: string, notes: FormalDistributeNotesDTO[]): Promise<FormalNoteDTO[]> => 
        this.reloadQueue.generateAndEnqueRequest(this.generateUpdateDistributeEvidenceDraft(formalId, notes));

    distributeEvidence = (formalId: string, notes: FormalDistributeNotesDTO[]): Promise<FormalNoteDTO[]> => 
        this.reloadQueue.generateAndEnqueRequest(this.generateDistributeEvidenceRequest(formalId, notes));

    private generateSaveEvidenceRequest = (
        note: FormalCreateNoteRequestDTO
    ) => () =>
        firstValueFrom(
            this.formalService.addUnassignedNote(note)
        ).catch((reason: any) => {
            throw reason;
        }).then(() => this.generateReloadEvidenceRequest(note.observationId)());

    private generatUpdateEvidenceRequest = (
        note: FormalUpdateNoteRequestDTO,
        observationId: string
    ) => () =>
        firstValueFrom(
            this.formalService.updateUnassignedNote(note)
        ).catch((reason: any) => {
            throw reason;
        }).then(() => this.generateReloadEvidenceRequest(observationId)())

    private generateDeleteEvidenceRequest = (
        noteId: string,
        observationId: string
    ) => () =>
        firstValueFrom(
            this.formalService.deleteUnassignedNote(noteId)
        ).catch((reason: any) => {
            throw reason;
        }).then(() => this.generateReloadEvidenceRequest(observationId)());

    private generateUpdateDistributeEvidenceDraft = (
        observationId: string, 
        notes: FormalDistributeNotesDTO[]
    ) => () =>
        firstValueFrom(
            this.formalService.updateDistributeEvidenceDraft(observationId, notes)
        ).catch((reason: any) => {
            throw reason;
        }).then(() => this.generateReloadEvidenceRequest(observationId)());

    private generateDistributeEvidenceRequest = (
        observationId: string, 
        notes: FormalDistributeNotesDTO[]
    ) => () =>
        firstValueFrom(
            this.formalService.distributeUnassignedNote(observationId, notes)
        ).then(() => this.observationCommentService.reloadComments())
        .catch((reason: any) => {
            throw reason;
        }).then(() => this.generateReloadEvidenceRequest(observationId)());

    private generateReloadEvidenceRequest = (
        observationId: string
    ) => () =>
        firstValueFrom(
            this.formalService.getNotes(observationId)
        ).then((notes: FormalNoteDTO[]) => {
            this.evidence.next(notes);
            return notes;
        }).catch((reason: any) => {
            throw reason;
        });
}
